/**
 * Support access, the operator's side: where owners have let support in, one
 * restaurant's grants and visits, and opening a session.
 *
 * Used only by `apps/platform-web`, under the operator's own session. Opening
 * a session answers with a link to the restaurant's back office carrying a
 * one-time code; open it straight away, once. With no live grant the answer
 * is 409, and the owner's consent is the only way round it.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';
import { useApi } from './provider';

export interface PlatformSupportGrantRow {
  id: string;
  restaurant_id: string;
  restaurant_name: string;
  restaurant_slug: string;
  granted_at: string;
  expires_at: string;
  allow_changes: boolean;
  note: string;
}

export interface PlatformSupportVisit {
  id: string;
  operator_name: string;
  read_only: boolean;
  started_at: string;
  expires_at: string;
  ended_at: string | null;
  ended_how: 'operator' | 'owner' | null;
  opened: boolean;
}

export interface PlatformSupportGrant {
  id: string;
  granted_by_name: string;
  granted_at: string;
  expires_at: string;
  allow_changes: boolean;
  note: string;
  ended_at: string | null;
  ended_by_name: string | null;
  live: boolean;
  sessions: PlatformSupportVisit[];
}

export interface RestaurantSupport {
  /** The live grant: a session can be opened now. Null: it cannot. */
  current: PlatformSupportGrant | null;
  grants: PlatformSupportGrant[];
}

export interface SupportOpened {
  /** The back office with a one-time code. Good once, for two minutes. */
  url: string;
  session_id: string;
  read_only: boolean;
  expires_at: string;
  mailed_to: string[];
  mail_failed: string[];
}

/** Restaurants whose owners have let support in now, soonest to end first. */
export function usePlatformSupportGrants() {
  const api = useApi();
  return useQuery({
    queryKey: ['platform', 'support'],
    queryFn: ({ signal }) => api.get<PlatformSupportGrantRow[]>('/v1/platform/support', signal),
  });
}

export function useRestaurantSupport(restaurantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['platform', 'support', restaurantId],
    queryFn: ({ signal }) =>
      api.get<RestaurantSupport>(`/v1/platform/restaurants/${restaurantId}/support`, signal),
  });
}

export function useOpenSupportSession() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<SupportOpened, ApiError, string>({
    mutationFn: (restaurantId) =>
      api.post<SupportOpened>(`/v1/platform/restaurants/${restaurantId}/support-sessions`),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['platform'] }),
  });
}
